import { BehaviorSubject } from 'rxjs';
import IBloc from '../../base/IBloc';
import { NotificationService } from '../../service/supabase/NotificationService';
import {
    Notification,
    NotificationFormData,
    NotificationStatistics
} from '../../types/cms.types';

export interface NotificationViewState {
    notifications: Notification[];
    pagination: {
        total: number;
        page: number;
        limit: number;
        totalPages: number;
    };
    filters: {
        topic?: string;
        status?: string;
        send_type?: string;
        search?: string;
    };
    statistics?: NotificationStatistics;
    loading: boolean;
    error: string | null;
    hasData: boolean;
}

export class BlocManagementNotifications extends IBloc {
    private _info: BehaviorSubject<NotificationViewState>;

    constructor() {
        super();
        this._info = new BehaviorSubject<NotificationViewState>({
            notifications: [],
            pagination: {
                total: 0,
                page: 1,
                limit: 10,
                totalPages: 0
            },
            filters: {},
            loading: false,
            error: null,
            hasData: false
        });
    }

    get info(): NotificationViewState {
        return this._info.getValue();
    }

    // Override getStream để return BehaviorSubject
    public getStream(key: string) {
        if (key === 'info') {
            return this._info;
        }
        return super.getStream(key);
    }

    get currentPagination() {
        return this.info.pagination;
    }

    /**
     * Initialize data - load notifications and statistics
     */
    async initData(): Promise<void> {
        await Promise.all([
            this.loadNotifications(),
            this.loadStatistics()
        ]);
    }

    /**
     * Load notifications with current filters and pagination
     */
    async loadNotifications(): Promise<void> {
        this._info.next({
            ...this.info,
            loading: true,
            error: null
        });

        try {
            const { pagination, filters } = this.info;

            const result = await NotificationService.getNotifications({
                page: pagination.page,
                limit: pagination.limit,
                ...filters
            });

            this._info.next({
                ...this.info,
                notifications: result.data,
                pagination: result.pagination,
                loading: false,
                hasData: result.data.length > 0
            });
        } catch (error: any) {
            this._info.next({
                ...this.info,
                loading: false,
                error: error.message || 'Failed to load notifications'
            });
        }
    }

    /**
     * Load statistics
     */
    async loadStatistics(): Promise<void> {
        try {
            const statistics = await NotificationService.getNotificationStatistics();
            this._info.next({
                ...this.info,
                statistics
            });
        } catch (error: any) {
            console.error('Failed to load statistics:', error);
        }
    }

    /**
     * Filter by topic
     */
    async filterByTopic(topic: string): Promise<void> {
        this._info.next({
            ...this.info,
            filters: {
                ...this.info.filters,
                topic: topic || undefined
            },
            pagination: {
                ...this.info.pagination,
                page: 1
            }
        });
        await this.loadNotifications();
    }

    /**
     * Filter by status
     */
    async filterByStatus(status: string): Promise<void> {
        this._info.next({
            ...this.info,
            filters: {
                ...this.info.filters,
                status: status || undefined
            },
            pagination: {
                ...this.info.pagination,
                page: 1
            }
        });
        await this.loadNotifications();
    }

    /**
     * Filter by send type
     */
    async filterBySendType(sendType: string): Promise<void> {
        this._info.next({
            ...this.info,
            filters: {
                ...this.info.filters,
                send_type: sendType || undefined
            },
            pagination: {
                ...this.info.pagination,
                page: 1
            }
        });
        await this.loadNotifications();
    }

    /**
     * Search notifications
     */
    async searchNotifications(searchTerm: string): Promise<void> {
        this._info.next({
            ...this.info,
            filters: {
                ...this.info.filters,
                search: searchTerm || undefined
            },
            pagination: {
                ...this.info.pagination,
                page: 1
            }
        });
        await this.loadNotifications();
    }

    /**
     * Clear all filters
     */
    async clearFilters(): Promise<void> {
        this._info.next({
            ...this.info,
            filters: {},
            pagination: {
                ...this.info.pagination,
                page: 1
            }
        });
        await this.loadNotifications();
    }

    /**
     * Change page
     */
    async changePage(page: number): Promise<void> {
        this._info.next({
            ...this.info,
            pagination: {
                ...this.info.pagination,
                page
            }
        });
        await this.loadNotifications();
    }

    /**
     * Change page size
     */
    async changePageSize(limit: number): Promise<void> {
        this._info.next({
            ...this.info,
            pagination: {
                ...this.info.pagination,
                page: 1,
                limit
            }
        });
        await this.loadNotifications();
    }

    /**
     * Refresh data
     */
    async refresh(): Promise<void> {
        await this.initData();
    }

    /**
     * Create new notification
     */
    async createNotification(
        notificationData: NotificationFormData,
        sendImmediately: boolean = false
    ): Promise<Notification> {
        try {
            const notification = await NotificationService.createNotification(notificationData);

            // If send immediately, trigger send
            if (sendImmediately && notificationData.send_type === 'immediate') {
                await NotificationService.sendNotificationNow(notification.id);
            }

            // Reload data
            await this.initData();

            return notification;
        } catch (error: any) {
            throw new Error(error.message || 'Failed to create notification');
        }
    }

    /**
     * Update notification
     */
    async updateNotification(
        id: string,
        updates: Partial<NotificationFormData>
    ): Promise<Notification> {
        try {
            const notification = await NotificationService.updateNotification(id, updates);
            await this.loadNotifications();
            return notification;
        } catch (error: any) {
            throw new Error(error.message || 'Failed to update notification');
        }
    }

    /**
     * Delete notification
     */
    async deleteNotification(id: string): Promise<void> {
        try {
            await NotificationService.deleteNotification(id);
            await this.initData();
        } catch (error: any) {
            throw new Error(error.message || 'Failed to delete notification');
        }
    }

    /**
     * Cancel scheduled notification
     */
    async cancelNotification(id: string): Promise<void> {
        try {
            await NotificationService.cancelNotification(id);
            await this.loadNotifications();
        } catch (error: any) {
            throw new Error(error.message || 'Failed to cancel notification');
        }
    }

    /**
     * Send notification now (for pending notifications)
     */
    async sendNotificationNow(id: string): Promise<void> {
        try {
            await NotificationService.sendNotificationNow(id);
            await this.initData();
        } catch (error: any) {
            throw new Error(error.message || 'Failed to send notification');
        }
    }

    /**
     * Resend failed notification
     */
    async resendNotification(id: string): Promise<void> {
        try {
            await NotificationService.resendNotification(id);
            await this.initData();
        } catch (error: any) {
            throw new Error(error.message || 'Failed to resend notification');
        }
    }

    /**
     * Update info stream (for external updates)
     */
    updateInfo(updates: Partial<NotificationViewState>): void {
        this._info.next({
            ...this.info,
            ...updates
        });
    }
}

export default BlocManagementNotifications;
